import { getCurrentScope, onScopeDispose } from 'vue'

/** Identifies one generation of a composable's requests; see {@link LatestRequest}. */
export type RequestToken = number

export interface LatestRequest {
  /** Starts a request: every earlier token stops being current. Use for loads that replace state. */
  begin: () => RequestToken
  /**
   * The token of the generation now in force, without starting a new one. Use for work that must
   * only be dropped when the context changed underneath it (a save, a load-more), not when another
   * save started.
   */
  current: () => RequestToken
  /** True while nothing has begun, invalidated, or disposed since `token` was issued. */
  isCurrent: (token: RequestToken) => boolean
  /** Drops every request in flight, e.g. when the id or source being viewed changes. */
  invalidate: () => void
}

/**
 * ORG-63 — the "ignore a stale response" guard. Take a token before awaiting, and apply the result
 * only if `isCurrent(token)` still holds afterwards. The guard also ends with the owning scope (a
 * component unmount or an effect scope stop), so a response arriving after unmount writes nothing.
 *
 * ```ts
 * const latest = useLatestRequest()
 * async function load() {
 *   const token = latest.begin()
 *   const result = await api.get()
 *   if (!latest.isCurrent(token)) return
 *   data.value = result
 * }
 * ```
 */
export function useLatestRequest(): LatestRequest {
  let generation = 0
  const invalidate = (): void => {
    generation += 1
  }
  if (getCurrentScope()) onScopeDispose(invalidate)
  return {
    begin: () => ++generation,
    current: () => generation,
    isCurrent: token => token === generation,
    invalidate
  }
}
